import React, { useRef, useState } from 'react';
import { Button } from '../ui/button';
import { Download, FileUp, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';

/**
 * Wiederverwendbare Import/Export-Buttons für beliebige Vorlagen-Listen
 * (PDF-Vorlagen, E-Mail-Vorlagen usw.).
 *
 * Zwei Komponenten:
 *  - <ImportButton/>     : Globaler Import-Button (oben in der Liste)
 *  - <ExportIconButton/> : Per-Zeile Export-Icon
 *
 * Beide nutzen die gleichen Endpoint-Konventionen:
 *  - Export:  GET  {basePath}/{id}/export   → liefert JSON (Stream)
 *  - Import:  POST {basePath}/import        → multipart/form-data feld "file"
 */

export function ImportButton({ basePath, label = 'JSON importieren', onImported, testIdPrefix = 'tpl' }) {
    const inputRef = useRef(null);
    const [busy, setBusy] = useState(false);

    const handleClick = () => inputRef.current?.click();

    const handleFile = async (e) => {
        const file = e.target.files?.[0];
        e.target.value = '';
        if (!file) return;
        setBusy(true);
        try {
            const fd = new FormData();
            fd.append('file', file);
            const r = await api.post(`${basePath}/import`, fd, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            toast.success(`„${r.data?.name || file.name}" importiert`);
            onImported?.(r.data);
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Import fehlgeschlagen');
        } finally {
            setBusy(false);
        }
    };

    return (
        <>
            <input
                ref={inputRef}
                type="file"
                accept="application/json,.json"
                className="hidden"
                onChange={handleFile}
                data-testid={`${testIdPrefix}-import-input`}
            />
            <Button
                variant="outline"
                size="sm"
                onClick={handleClick}
                disabled={busy}
                data-testid={`${testIdPrefix}-import-btn`}
            >
                {busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <FileUp className="h-4 w-4 mr-1.5" />}
                {label}
            </Button>
        </>
    );
}

export function ExportIconButton({ basePath, id, name, fileNamePrefix = 'export', testIdPrefix = 'tpl' }) {
    const [busy, setBusy] = useState(false);

    const handleClick = async (e) => {
        e.stopPropagation();
        e.preventDefault();
        setBusy(true);
        try {
            const r = await api.get(`${basePath}/${id}/export`, { responseType: 'blob' });
            const url = URL.createObjectURL(r.data);
            const a = document.createElement('a');
            const safeName = (name || 'export').replace(/[^\w\-]+/g, '_').replace(/^_+|_+$/g, '');
            a.href = url;
            a.download = `${fileNamePrefix}-${safeName || 'item'}.json`;
            document.body.appendChild(a);
            a.click();
            URL.revokeObjectURL(url);
            document.body.removeChild(a);
            toast.success(`„${name || 'Vorlage'}" exportiert`);
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Export fehlgeschlagen');
        } finally {
            setBusy(false);
        }
    };

    return (
        <button
            type="button"
            title="Als JSON-Datei exportieren"
            onClick={handleClick}
            disabled={busy}
            className="p-1.5 rounded hover:bg-gray-100 text-gray-500 hover:text-gray-800 transition-colors disabled:opacity-50"
            data-testid={`${testIdPrefix}-export-${id}`}
        >
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
        </button>
    );
}

export default { ImportButton, ExportIconButton };
